//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  IContentLibrarySourcesOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import { CampaignsFilter } from '@components/media-filters/CampaignsFilter';
import { DistributionFilter } from '@components/media-filters/DistributionFilter';
import { MediaTypeFilter } from '@components/media-filters/MediaTypeFilter';
import { OrientationFilter } from '@components/media-filters/OrientationFilter';
import { ProductTagsFilter } from '@components/media-filters/ProductTagsFilter';
import { RightsFilter } from '@components/media-filters/RightsFilter';
import { SelectedFilters } from '@components/media-filters/SelectedFilters';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { LabelsFilter } from '@content-drawer/filters/LabelsFilter';
import { getDisabledFilters } from '@content-drawer/selectors/getContentDrawerSettings';

export interface MediaFilterPanelProps {
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  onChangeSources: (value: IContentLibrarySourcesOptions) => void;
  onReset: () => void;
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>;
  sources: IContentLibrarySourcesOptions;
}

export const MediaFilterPanel = ({
  onChange,
  onChangeSources,
  onReset,
  sections,
  sources,
}: MediaFilterPanelProps) => {
  const disabledFilters = useSelector(getDisabledFilters);

  const handleOnChange = (section: FilterSection<FilterValue>) => {
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    updatedSections.set(section.paramKey, section);
    onChange(updatedSections);
  };

  return (
    <Panel>
      <SelectedFilters
        onChange={onChange}
        onReset={onReset}
        sections={sections}
      />
      <LabelsFilter
        isDisabled={disabledFilters?.includes('labels')}
        onChange={onChange}
        section={getSection(sections, 'labels')}
        sections={sections}
        sources={sources}
      />
      <ProductTagsFilter
        isDisabled={disabledFilters?.includes('hasProducts')}
        onChange={onChange}
        section={getSection(sections, 'hasProducts')}
        sections={sections}
      />
      <DistributionFilter
        distributedToSection={getSection(sections, 'distribution')}
        distributionSection={getSection(sections, 'isDistributed')}
        isDisabledDistributedTo={disabledFilters?.includes('distribution')}
        isDisabledDistribution={disabledFilters?.includes('isDistributed')}
        onChange={handleOnChange}
      />
      <RightsFilter
        isDisabled={disabledFilters?.includes('rightsStatuses')}
        onChange={onChange}
        section={getSection(sections, 'rightsStatuses')}
      />
      <CampaignsFilter
        onChange={onChange}
        section={getSection(sections, 'campaignTypes')}
        subsection={getSection(sections, 'campaignSubTypes')}
      />
      <MediaTypeFilter
        isDisabled={disabledFilters?.includes('mediaItemTypes')}
        onChange={onChange}
        section={getSection(sections, 'mediaItemTypes')}
      />
      <OrientationFilter
        isDisabled={disabledFilters?.includes('orientation')}
        onChange={onChange}
        section={getSection(sections, 'orientation')}
      />
    </Panel>
  );
};

const Panel = styled.div(
  ({ theme }) => `
    height: 100%;
    width: 270px;
    background: ${theme.colors.white};
    overflow-y: auto;
  `,
);
